import React, { useCallback, useContext, useMemo } from 'react';
import { Typography } from '@vtblife/uikit';
import Grid from '@vtblife/uikit-grid';

import classname from '@search/classname/src';
import { useStaticPathConfig } from '@search/common-core/src/StaticPathContext';
import { useToggleValue } from '@search/hooks/src/useToggleValue';
import type { NewBuildingSnippet } from '@search/graphql-typings';

import { useClassifiedOfferDataLayerPush } from '../../../../offer/analytics/ClassifiedOfferGtmContext';
import Image from '../../../../../view/common/components/Image';
import GallerySnippet from '../../../../../view/desktop/components/snippet/parts/GallerySnippet';
import { getNewBuildingLink } from '../../../../../view/selectors/get_new_building_link';
import { getDeveloperLink } from '../../../../../view/selectors/get_developer_link';
import { dataLayerPush as regularDataLayerPush } from '../../../../google/dataLayerPush';
import type {
    SearchOffersQuery$data as SearchOffersQueryResponse
} from '../../../../offer/search/page/SearchOffersQuery/__generated__/SearchOffersQuery.graphql';
import type {
    SearchOffersQueryNBInfoItemFields$data as SearchOffersQueryNBInfoItemFields
} from '../../../../offer/search/page/SearchOffersQuery/__generated__/SearchOffersQueryNBInfoItemFields.graphql';

import { useNewBuilding } from '../../../hooks/useNewBuilding';
import { DATA_GTM } from '../../../libs/constants';

import { List, ListItem } from '../../common/List';
import SnippetPhone from '../../common/SnippetPhone';

import MetroRoute, { IMetroRouteProps } from '../../common/MetroRoute';
import IconTransport, { IconTransportProps } from '../../common/IconTransport';

import GalleryCapImage from './cap.url.png';
import { SnippetContext } from './snippetContext';
import DefaultDeveloperIcon from './DefaultDeveloperIcon.url.svg';

import './styles.css';

interface INewBuildingSnippet {
    newBuilding: SearchOffersQueryResponse['newBuilding'];
}

const cn = classname.bind(null, 'NewBuildingBaseSnippet');

export const renderMetroRoutes = (
    routes: NewBuildingSnippet['routes']
) => {
    if (! (routes && routes?.length)) {
        return null;
    }

    const [ metro ] = routes;

    if (! metro) {
        return null;
    }

    return (
        <div
            key='metro'
            className={cn('metro')}
        >
            <MetroRoute
                metro={metro as IMetroRouteProps['metro']}
                className={cn('metroRoute')}
                withoutTransport
            />
            <Typography
                tag='span'
                color='secondary'
            >
                <IconTransport
                    transportType={metro.transportType as IconTransportProps['transportType']}
                    color='#a4a1b5'
                />
                {`${metro.timeMinutes} мин.`}
            </Typography>
        </div>
    );
};

interface IDeveloper {
    id: number;
    title: string;
    translitTitle: string;
    icon: {
        origin: string;
    } | null;
}

const NewBuildingBaseSnippetDeveloper = ({ developer } : { developer: IDeveloper}) => {
    const staticPath = useStaticPathConfig();

    return (
        <div className={cn('developerName')}>
            {developer.icon?.origin ? (
                <Image
                    type='contain'
                    width={40}
                    height={40}
                    url={developer.icon.origin}
                />
            ) : (
                <div className={'Image'} style={{
                    width: '40px',
                    height: '40px'
                }}>
                    <Image
                        loading='lazy'
                        url={`${staticPath}${DefaultDeveloperIcon}`}
                    />
                </div>
            )}
            {developer.title}
        </div>
    );
};

const NewBuildingBaseSnippetDeveloperWithLink = ({ developer } : { developer: IDeveloper }) => {
    const developerUrl = useMemo(
        () => getDeveloperLink({ name: developer.translitTitle, id: developer.id }),
        [ developer ]
    );

    if (! developerUrl) {
        return (
            <div className={cn('developer')}>
                <NewBuildingBaseSnippetDeveloper developer={developer} />
            </div>
        );
    }

    return (
        <a
            target='_blank'
            className={cn('developer')}
            href={developerUrl}
        >
            <NewBuildingBaseSnippetDeveloper developer={developer} />
        </a>
    );
};

export type NewBuildingBaseSnippetProps = INewBuildingSnippet & {
    calltouchId?: string;
    gtmClick?: string;
};

const NewBuildingBaseSnippet: React.FunctionComponent<NewBuildingBaseSnippetProps> = ({
    newBuilding,
    calltouchId,
    gtmClick
}) => {
    const snippetContextValue = useContext(SnippetContext)!;
    const {
        hero,
        routes,
        isFrozen,
        developer
    } = useNewBuilding(newBuilding);

    const { region, regionId, subRegion, type, name, id } = newBuilding?.routeParams!;

    const url = useMemo(
        () => getNewBuildingLink({ region, regionId, subRegion, type, name, id }),
        [ region, regionId, subRegion, type, name, id ]
    );

    const dataLayerPush = useClassifiedOfferDataLayerPush();

    const sendAnalytics = useCallback(() => {
        gtmClick && dataLayerPush({ event: gtmClick });
    }, [ gtmClick, dataLayerPush ]);

    const handleGalleryClick = useCallback(() => {
        window.open(url, '_blank');
    }, [ url ]);

    const staticPath = useStaticPathConfig();

    const hoverToggle = useToggleValue(false);

    const info = newBuilding?.info;

    const infoItems = useMemo(
        () => [
            info?.commissioningDate,
            info?.apartments,
            info?.buildingClass,
            info?.totalCount
        ]
            .filter(Boolean),
        [ info ]
    );

    const onStatEventPush = useCallback(() => regularDataLayerPush({
        event: 'nb_call_like',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        nb_price: newBuilding?.priceMin ?? 0,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        nb_class: newBuilding?.ranking ?? ''
    }), [ newBuilding ]);

    if (! newBuilding) {
        return null;
    }

    return (
        <div className={cn()}>
            <a
                target='_blank'
                href={url}
                key='nb-link'
                className={cn('link')}
                onClick={sendAnalytics}
                onMouseOver={hoverToggle.handleSetTrueTogVal}
                onMouseLeave={hoverToggle.handleSetFalseTogVal}
            />
            <Grid cols={2}>
                <Grid.Cell>
                    <div className={cn('leftBlock')}>
                        <div className={cn('content')}>
                            <List
                                className={cn('list')}
                                layout='vertical'>
                                {[
                                    <Typography
                                        variant='h3'
                                        tag='span'
                                        className={cn('title', { active: hoverToggle.isToggleValue })}
                                        key='nb-title'
                                    >
                                        {newBuilding.title.nominative}
                                    </Typography>,
                                    <Typography
                                        tag='span'
                                        data-gtm={snippetContextValue!.state.isOpenMap ?
                                            null : DATA_GTM.SNIPPET_MAP
                                        }
                                        key='address'
                                    >
                                        {newBuilding.address}
                                    </Typography>,
                                    renderMetroRoutes(routes as NewBuildingSnippet['routes'])
                                ].filter(Boolean).map((item, idx) =>
                                    <ListItem className={cn('list-item')} key={idx}>{item}</ListItem>
                                )}
                            </List>
                            <p className={cn('description')}>
                                {newBuilding.snippetDescription}
                            </p>
                            <div className={cn('info')}>
                                {(infoItems as Array<SearchOffersQueryNBInfoItemFields | null>).map((item, idx) => (
                                    <div className={cn('infoItem', { extra: idx > 2 })} key={idx}>
                                        <Typography
                                            className={cn('infoItemLabel')}
                                            variant='secondary-alone'
                                            color='secondary'
                                        >
                                            <div dangerouslySetInnerHTML={{
                                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                                __html: item!.snippetLabel ?? item!.label
                                            }} />
                                        </Typography>
                                        <Typography
                                            className={cn('infoItemValue')}
                                            variant='primary-alone'
                                            color={isFrozen && idx === 0 ? 'red-500' : undefined}
                                        >
                                            <div dangerouslySetInnerHTML={{
                                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                                __html: item!.snippetValue ?? item!.value
                                            }} />
                                        </Typography>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {developer && (
                            <div className={cn('footer')}>
                                <div
                                    className={cn('button')}
                                    onMouseOver={hoverToggle.handleSetTrueTogVal}
                                    onMouseLeave={hoverToggle.handleSetFalseTogVal}
                                >
                                    <SnippetPhone
                                        phoneParams={{
                                            phoneNum: 0,
                                            developerId: developer.id,
                                            newBuildingId: Number(newBuilding.routeParams!.id)
                                        }}
                                        calltouchId={calltouchId}
                                        onPhoneEventPush={onStatEventPush}
                                        variant={hoverToggle.isToggleValue ? 'primary' : 'secondary'}
                                        fullWidth
                                    />
                                </div>
                                <NewBuildingBaseSnippetDeveloperWithLink developer={developer as IDeveloper} />
                            </div>
                        )}
                    </div>
                </Grid.Cell>
                <Grid.Cell>
                    <div className={cn('gallery')}>
                        {
                            hero?.src ? (
                                <GallerySnippet
                                    className={cn('gallerySnippet')}
                                    pictures={[ hero ]}
                                    isFrozen={isFrozen}
                                    onClick={handleGalleryClick}
                                    withLazyLoad
                                />
                            ) : (
                                <img
                                    crossOrigin='anonymous'
                                    alt='нет фотографии ЖК, заглушка'
                                    src={`${staticPath}${GalleryCapImage}`}
                                    className={cn('galleryCap')}
                                />
                            )
                        }
                    </div>
                </Grid.Cell>
            </Grid>
        </div>
    );
};

export default NewBuildingBaseSnippet;
